import { uuid } from '../core/ids.js';
import { localDate, nowIso, tzOffsetMinutes } from '../core/time.js';
import { logEvent, rescueStats } from '../storage/db.js';
import { recordRescueOnEpisode } from '../episodes/episode-store.js';
import { getModule } from './rescue-registry.js';
import type { ConsumptionMethodRecord, RescueRunSummary } from '../core/types.js';
import type { GeneratedChallenge } from './challenge-types.js';

export interface RunRescueOptions {
  moduleId: string;
  habitId: string;
  planId?: string | undefined;
  method?: ConsumptionMethodRecord | undefined;
  durationSec?: number | undefined;
  reducedMotion: boolean;
  motivation?: string | undefined;
  episodeId?: string | undefined;
  challenge?: GeneratedChallenge | undefined;
}

export interface RunRescueResult {
  sessionId: string;
  moduleId: string;
  moduleKind: string;
  result: 'completed' | 'abandoned';
  durationMs: number;
  details: Record<string, unknown>;
  summary: RescueRunSummary;
}

/**
 * Encapsulated rescue module launch. Logs rescue_started/completed/abandoned
 * with episode + challenge linkage, updates per-module stats, and returns a
 * normalized summary the caller can hand to the challenge engine.
 */
export async function runRescueModule(
  host: HTMLElement,
  opts: RunRescueOptions,
): Promise<RunRescueResult> {
  const mod = getModule(opts.moduleId);
  if (!mod) throw new Error(`Rescue module not found: ${opts.moduleId}`);
  const sessionId = uuid();
  const startAt = new Date();
  const methodPayload = opts.method
    ? { methodId: opts.method.id, methodKind: opts.method.kind }
    : {};
  const challengePayload = opts.challenge
    ? { challengeId: opts.challenge.id, challengeMetric: opts.challenge.metric }
    : {};

  await logEvent({
    type: 'rescue_started',
    habitId: opts.habitId,
    ...(opts.planId ? { planId: opts.planId } : {}),
    ...(opts.episodeId ? { episodeId: opts.episodeId } : {}),
    occurredAt: startAt.toISOString(),
    localDate: localDate(startAt),
    timezoneOffsetMinutes: tzOffsetMinutes(startAt),
    payload: {
      rescueSessionId: sessionId,
      moduleId: opts.moduleId,
      moduleKind: mod.kind,
      plannedDurationSec: opts.durationSec ?? mod.defaultDurationSec,
      ...methodPayload,
      ...challengePayload,
      ...(opts.episodeId ? { episodeId: opts.episodeId } : {}),
    },
  });

  let result;
  try {
    result = await mod.run(host, {
      durationSec: opts.durationSec ?? mod.defaultDurationSec,
      reducedMotion: opts.reducedMotion,
      motivation: opts.motivation,
    });
  } catch (err) {
    // Module threw mid-run. Log abandonment so rescue_started is paired and
    // stats don't drift, then rethrow so the caller can surface the error.
    const endAt = new Date();
    await logEvent({
      type: 'rescue_abandoned',
      habitId: opts.habitId,
      ...(opts.planId ? { planId: opts.planId } : {}),
      ...(opts.episodeId ? { episodeId: opts.episodeId } : {}),
      occurredAt: endAt.toISOString(),
      localDate: localDate(endAt),
      timezoneOffsetMinutes: tzOffsetMinutes(endAt),
      payload: {
        rescueSessionId: sessionId,
        moduleId: opts.moduleId,
        moduleKind: mod.kind,
        durationMs: endAt.getTime() - startAt.getTime(),
        crashed: true,
        ...methodPayload,
        ...challengePayload,
        ...(opts.episodeId ? { episodeId: opts.episodeId } : {}),
      },
    });
    throw err;
  }

  const endAt = new Date();
  const durationMs = endAt.getTime() - startAt.getTime();
  const details: Record<string, unknown> = result.details ?? {};
  const summary: RescueRunSummary = {
    moduleId: opts.moduleId,
    moduleKind: mod.kind,
    result: result.kind,
    durationMs,
    metrics: extractMetrics(details),
  };

  await logEvent({
    type: result.kind === 'completed' ? 'rescue_completed' : 'rescue_abandoned',
    habitId: opts.habitId,
    ...(opts.planId ? { planId: opts.planId } : {}),
    ...(opts.episodeId ? { episodeId: opts.episodeId } : {}),
    occurredAt: endAt.toISOString(),
    localDate: localDate(endAt),
    timezoneOffsetMinutes: tzOffsetMinutes(endAt),
    payload: {
      rescueSessionId: sessionId,
      moduleId: opts.moduleId,
      moduleKind: mod.kind,
      durationMs,
      runSummary: summary,
      ...methodPayload,
      ...challengePayload,
      ...details,
      ...(opts.episodeId ? { episodeId: opts.episodeId } : {}),
    },
  });

  // Update per-habit module stats. Mirrors the prior rescue-screen behavior.
  const all = await rescueStats.byHabit(opts.habitId);
  const cur = all.find((s) => s.moduleId === opts.moduleId) ?? {
    id: uuid(), habitId: opts.habitId, moduleId: opts.moduleId, kind: mod.kind,
    starts: 0, completions: 0, abandonments: 0,
    completedThenWaited: 0, completedThenUsed: 0,
    averageDurationMs: 0, updatedAt: nowIso(),
  };
  cur.starts += 1;
  if (result.kind === 'completed') cur.completions += 1; else cur.abandonments += 1;
  cur.averageDurationMs = Math.round(
    (cur.averageDurationMs * (cur.starts - 1) + durationMs) / cur.starts,
  );
  cur.lastUsedAt = endAt.toISOString();
  cur.updatedAt = nowIso();
  await rescueStats.put(cur);

  if (opts.episodeId) {
    await recordRescueOnEpisode(
      opts.episodeId, sessionId, durationMs, result.kind === 'completed',
    );
  }

  return {
    sessionId,
    moduleId: opts.moduleId,
    moduleKind: mod.kind,
    result: result.kind,
    durationMs,
    details,
    summary,
  };
}

function extractMetrics(details: Record<string, unknown>): Record<string, number | boolean> {
  const out: Record<string, number | boolean> = {};
  for (const [k, v] of Object.entries(details)) {
    if (typeof v === 'number' || typeof v === 'boolean') out[k] = v;
  }
  return out;
}
